<template>
  <div class="settings-payment mt-1 mb-3">
    <h3 class="mb-1">{{ $t("settings.payment-methods.title") }}</h3>

    <!-- Keep pointerdown away from the scroll dialog overlay: it cancels it, which blocks the drag (L3-8100) -->
    <draggable
      v-model="settingsData"
      handle=".handle"
      item-key="id"
      @pointerdown.stop
    >
      <template #item="{ element }">
        <SettingsItem
          :settingInfo="element"
          :ref="
            (el) => {
              if (el) paymentForms[i] = el;
            }
          "
        ></SettingsItem>
      </template>
    </draggable>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUpdate, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import draggable from "vuedraggable";

// modules
import POSConfigModule from "@/store/modules/pos-config";

// constants
import { Currencies } from "@/constants/currency";
import { IPaymentConfig, PaymentMethodTypes } from "@/types/db/pos-config";
import { getLocale } from "@/lang";

import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "SettingsPaymentMethods",
  components: { draggable },
  setup() {
    const { t } = useI18n({ useScope: "global" });
    const paymentForms = ref([]);
    const drag = ref(false);

    const posConfig = POSConfigModule.selectedConfig;
    const groupId = computed(() => {
      return POSConfigModule.groupId;
    });

    const paymentConfig: IPaymentConfig = posConfig?.paymentConfig || {
      cash: {
        used: true,
        acceptedCurrencies: ["CHF"] as Currencies[],
        // acceptedBills: Partial<FieldCurrencyType>,
      },
      cardAuto: {
        used: false,
        processor: "ADYEN",
      },
      cardManual: {
        used: true,
        paymentMethods: ["VISA", "TWINT"],
      },
      invoice: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      toBeInvoiced: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      bankTransfer: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      cheque: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      chequeVacances: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      chequeCulture: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      passCulture: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      chorus: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      CE: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      OCCYG: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      couponSport: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      posReka: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      diverseVoucher: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      terminal: "",
      CFF: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      CGN: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      QOQA: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      DEINDEAL: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      REALDEAL: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      EXTERNAL_INCERT_VOUCHER: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      Swiss_QR_Code_Bank_Transfer: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      buyClub: {
        used: false,
        acceptedStatuses: ["Pending"],
      },

      POS_BIMPLI_CADO: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      POS_KADEOS_INFINI: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      POS_ANCV_CONNECT: {
        used: false,
        acceptedStatuses: ["Pending"],
      },

      TOUT_UN_CANTON: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      CHAPLINS: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      HELLO_CSE: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
      Z_CH_GUTSCHEIN: {
        used: false,
        acceptedStatuses: ["Pending"],
      },
    };

    // This is the default payment settigns array
    const settingsArray = [
      {
        id: PaymentMethodTypes.Cash,
        name: t("button.cash"),
        used: paymentConfig.cash.used,
        currency: paymentConfig.cash.acceptedCurrencies,
        terminal: null,
        // paymentMethods: null,
      },
      {
        id: PaymentMethodTypes.CardAuto,
        name: t("button.card"),
        used: paymentConfig.cardAuto.used,
        // currency: null,
        terminal: paymentConfig.terminal,
        // paymentMethods: null,
      },
      {
        id: PaymentMethodTypes.CardManual,
        name: `${t("button.card")} ${t("button.manual-processing")}`,
        used: paymentConfig.cardManual.used,
        // currency: null,
        terminal: null,
        paymentMethods: paymentConfig.cardManual.paymentMethods,
      },
      {
        id: PaymentMethodTypes.Invoice,
        name: t("button.invoice"),
        used: paymentConfig.invoice?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.invoice?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.ToBeInvoiced,
        name: t("payment-method.TO_BE_INVOICED"),
        used: paymentConfig.toBeInvoiced?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.toBeInvoiced?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.BankTransfer,
        name:
          t("button.bank-transfer") +
          (groupId.value === 19329 ? "\n/ Bon cadeau (ancien)" : ""),
        used: paymentConfig.bankTransfer?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.bankTransfer?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.PosReka,
        name: t("payment-method.POS_REKA"),
        used: paymentConfig.posReka?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.posReka?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.DiverseVoucher,
        name: t("payment-method.POS_DIVERSE_VOUCHER"),
        used: paymentConfig.diverseVoucher?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.diverseVoucher?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.POS_ANCV_CONNECT,
        name: t("payment-method.POS_ANCV_CONNECT"),
        used: paymentConfig.POS_ANCV_CONNECT?.used,
        terminal: null,
        acceptedStatuses:
          paymentConfig.POS_ANCV_CONNECT?.acceptedStatuses || [],
      },
      // only for Edeis
      ...((groupId.value && [1, 16342, 16383].includes(groupId.value)) ||
      POSConfigModule.isEdeis
        ? [
            {
              id: PaymentMethodTypes.POS_BIMPLI_CADO,
              name: t("payment-method.POS_BIMPLI_CADO"),
              used: paymentConfig.POS_BIMPLI_CADO?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.POS_BIMPLI_CADO?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.POS_KADEOS_INFINI,
              name: t("payment-method.POS_KADEOS_INFINI"),
              used: paymentConfig.POS_KADEOS_INFINI?.used,
              terminal: null,
              acceptedStatuses:
                paymentConfig.POS_KADEOS_INFINI?.acceptedStatuses || [],
            },
          ]
        : []),
      ...(POSConfigModule.getGroupCountryCode === "FR"
        ? [
            {
              id: PaymentMethodTypes.Cheque,
              name: t("button.cheque"),
              used: paymentConfig.cheque?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.cheque?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.ChequeVacances,
              name: t("button.cheque-vacances"),
              used: paymentConfig.chequeVacances?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.chequeVacances?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.chequeCulture,
              name: t("button.cheque-culture"),
              used: paymentConfig.chequeCulture?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.chequeCulture?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.passCulture,
              name: t("button.pass-culture"),
              used: paymentConfig.passCulture?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.passCulture?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.chorus,
              name: t("button.chorus"),
              used: paymentConfig.chorus?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.chorus?.acceptedStatuses,
            },
          ]
        : []),
      //
      ...(groupId.value &&
      [1, 16508, 16342, 16383, /*Eis Greisler*/ 19652].includes(groupId.value)
        ? [
            {
              id: PaymentMethodTypes.EXTERNAL_INCERT_VOUCHER,
              name: t("payment-method.Incert_Voucher"),
              used: paymentConfig.EXTERNAL_INCERT_VOUCHER?.used,
              terminal: null,
              acceptedStatuses:
                paymentConfig.EXTERNAL_INCERT_VOUCHER?.acceptedStatuses,
            },
          ]
        : []),
      //
      ...(groupId.value &&
      [1, 16342, 16383, /*Fort Chillion*/ 19657].includes(groupId.value)
        ? [
            {
              id: PaymentMethodTypes.CFF,
              name: t("payment-method.cff"),
              used: paymentConfig.CFF?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.CFF?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.CGN,
              name: t("payment-method.cgn"),
              used: paymentConfig.CGN?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.CGN?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.QOQA,
              name: t("payment-method.qoqa"),
              used: paymentConfig.QOQA?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.QOQA?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.DEINDEAL,
              name: t("payment-method.deindeal"),
              used: paymentConfig.DEINDEAL?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.DEINDEAL?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.REALDEAL,
              name: t("payment-method.realdeal"),
              used: paymentConfig.REALDEAL?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.REALDEAL?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.BuyClub,
              name: t("payment-method.BUY_CLUB"),
              used: paymentConfig.buyClub?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.buyClub?.acceptedStatuses,
            },
          ]
        : []),
      ...(import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev" ||
      POSConfigModule.groupInfo?.brandingList?.[0]?.country?.alpha2 === "CH"
        ? [
            {
              id: PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer,
              name: t("payment-method.BANK_TRANSFER_SWISS_QR"),
              used: paymentConfig.Swiss_QR_Code_Bank_Transfer?.used,
              terminal: null,
              acceptedStatuses:
                paymentConfig.Swiss_QR_Code_Bank_Transfer?.acceptedStatuses,
            },
          ]
        : []),
      ...(groupId.value && [19360, 16342, 16383].includes(groupId.value)
        ? [
            {
              id: PaymentMethodTypes.CE,
              name: t("button.CE"),
              used: paymentConfig.CE?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.CE?.acceptedStatuses,
            },
            {
              id: PaymentMethodTypes.OCCYG,
              name: t("button.OCCYG"),
              used: paymentConfig.OCCYG?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.OCCYG?.acceptedStatuses,
            },
          ]
        : []),
      ...(groupId.value &&
      [
        1, 16342 /*DEV*/, 16383 /*TEST*/, 19345 /* Trampoline Spirit */,
      ].includes(groupId.value)
        ? [
            {
              id: PaymentMethodTypes.CouponSport,
              name: t("button.coupon-sport"),
              used: paymentConfig.couponSport?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.couponSport?.acceptedStatuses,
            },
          ]
        : []),

      {
        id: PaymentMethodTypes.TOUT_UN_CANTON,
        name: t("payment-method.TOUT_UN_CANTON"),
        used: paymentConfig.TOUT_UN_CANTON?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.TOUT_UN_CANTON?.acceptedStatuses,
      },
      {
        id: PaymentMethodTypes.CHAPLINS,
        name: t("payment-method.CHAPLINS"),
        used: paymentConfig.CHAPLINS?.used,
        terminal: null,
        acceptedStatuses: paymentConfig.CHAPLINS?.acceptedStatuses,
      },
      ...(POSConfigModule.isEclipsoLyon
        ? [
            {
              id: PaymentMethodTypes.HELLO_CSE,
              name: t("payment-method.HELLO_CSE"),
              used: paymentConfig.HELLO_CSE?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.HELLO_CSE?.acceptedStatuses,
            },
          ]
        : []),
      ...(POSConfigModule.isVHS
        ? [
            {
              id: PaymentMethodTypes.Z_CH_GUTSCHEIN,
              name: t("payment-method.Z_CH_GUTSCHEIN"),
              used: paymentConfig.Z_CH_GUTSCHEIN?.used,
              terminal: null,
              acceptedStatuses: paymentConfig.Z_CH_GUTSCHEIN?.acceptedStatuses,
            },
          ]
        : []),
    ]; // settingsArray

    // in case we added new payment methods, we should add them while keeping the order and settings of the user
    let newPaymentMethods: any = [];
    if (POSConfigModule.getPaymentSettings.length) {
      for (let i = 0; i < settingsArray.length; i++) {
        let exists = false;
        for (let j = 0; j < POSConfigModule.getPaymentSettings.length; j++) {
          if (
            settingsArray[i].id === POSConfigModule.getPaymentSettings[j].id
          ) {
            exists = true;
            break;
          }
        }
        if (!exists) {
          newPaymentMethods.push(settingsArray[i]);
        }
      }
    }
    const settingsData = ref(
      cloneDeep(
        POSConfigModule.getPaymentSettings?.length
          ? [...POSConfigModule.getPaymentSettings, ...newPaymentMethods]
          : settingsArray
      )
    );

    // get Adyen terminals
    POSConfigModule.fetchGroupInfo();

    onBeforeUpdate(() => {
      paymentForms.value = [];
    });

    return {
      paymentForms,
      settingsData,
      drag,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.settings-payment {
  .el-dialog {
    min-width: 750px;
    max-width: 1000px;
  }

  h3 {
    line-height: 22px;
    letter-spacing: 0px;
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: left;
  }
}
</style>
